import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { CancelSplash } from './CancelSplash.js';
import {
  ArrowRightIcon,
  LockIcon,
  MicIcon,
  MicOffIcon,
  PauseIcon,
  TrashIcon,
  WaveformIcon,
  XIcon,
} from './icons.js';
import type { VoiceComposerLabels } from './labels.js';
import { useListeningCues } from './listeningCues.js';
import { LiveWaveform } from './LiveWaveform.js';
import { RecIndicator } from './RecIndicator.js';
import { ScrollingWave, TakeOverview, fmtTime } from './TakeWaves.js';
import { useHoldGesture } from './useHoldGesture.js';
import type { FreeChat } from './useFreeChat.js';
import type { HoldRecorder } from './useHoldTurn.js';
import './ptt.css';

const zeroEnergy = (): number => 0;

/** Ventana del escudo anti ghost-click del panel fijado (ver abajo). El click
 *  fantasma llega <100ms después del pointerup; 400ms dan margen holgado sin
 *  frenar un segundo tap humano deliberado. */
const GHOST_CLICK_MS = 400;

export interface VoiceComposerProps {
  recorder: HoldRecorder;
  /** Bloquea el hold (conectando / turno en vuelo). El texto nunca se bloquea. */
  holdDisabled: boolean;
  /** Mic denegado / canal 'text' → composer sólo-texto (sin botón de hold). */
  micHidden: boolean;
  /** Canal 'voice' → composer sólo-PTT: oculta el campo de texto y el botón de
   *  hold ocupa el ancho (pill). Simétrico de `micHidden`. */
  textHidden?: boolean;
  placeholder: string;
  onSendText: (text: string) => void;
  /**
   * Strings del composer, ya resueltos por idioma en la app (en fit: `t.ptt`).
   * Requerido a propósito — el package no trae textos default (fail-fast).
   */
  labels: VoiceComposerLabels;
  /**
   * Modo charla a manos libres (`useFreeChat`). Presente = el composer ofrece
   * la entrada (botón de ondas en el campo) y renderiza la barra viva cuando
   * está activo. Ausente (onboarding) = composer clásico sin el modo.
   */
  freeChat?: FreeChat;
  /** Estado del pipeline para la barra viva del modo charla. */
  freeStatus?: 'listening' | 'thinking' | 'speaking';
  /**
   * Contador que la app sube cuando el server confirma un turno del alumno
   * (transcript final). Pasarlo suma a la barra viva las señales de escucha
   * (`listeningCues.ts`): el punto asiente en la pausa del alumno y, con el
   * turno cerrado, la barra dice `labels.heard` antes de «pensando». Exige
   * `labels.heard` (fail-fast). Una app sin esa señal (fit) no lo pasa.
   */
  turnClosedTick?: number;
  /**
   * Slot a la IZQUIERDA de todo el composer (fuera del campo), alineado con
   * el campo y el mic — pensado para un botón circular (ej. "+"). Sólo se
   * muestra en el estado default: en grabación/charla/descarte la fila se
   * reemplaza entera y el slot desaparece con el campo.
   */
  leading?: ReactNode;
  /** Slot DENTRO del campo pill, a la izquierda del input — pensado para un
   *  botón ícono chico (ej. lamparita). Desaparece junto con el campo. */
  fieldStart?: ReactNode;
  /**
   * Estado TERMINAL (ej. la sesión cerró server-side): apaga input de texto,
   * envío, mic y entrada a charla. A diferencia de `holdDisabled` (transitorio,
   * el texto sigue vivo), acá se inhabilita todo — sin esto un input ya
   * focuseado seguiría aceptando Enter y el mensaje se perdería en silencio.
   */
  disabled?: boolean;
}

/**
 * Composer compartido de Orborix (estándar de barra inferior de las apps de
 * voz; extraído del chat del PT de fit): campo de texto a la izquierda +
 * botón push-to-talk a la derecha — las dos únicas entradas.
 * Estilo WhatsApp: mantené el mic para grabar (el campo se reemplaza por la
 * barra de grabación), soltá para enviar, deslizá a la izquierda para
 * cancelar, deslizá hacia ARRIBA — o TAP RÁPIDO — para fijar. Fijada, el
 * composer se convierte en el panel de dos filas de WhatsApp: tiempo + onda
 * arriba; tacho / pausa / enviar abajo.
 *
 * Si la conexión se cae a mitad del turno, el recorder lo fija y lo pausa solo
 * (`recorder.interrupted`): el panel muestra el aviso y deja sólo el tacho —
 * ese audio no viajó y no hay forma de reenviarlo (ver `useHoldTurn`).
 *
 * El botón del mic PERSISTE entre idle/recording (pointer capture del hold);
 * recién al FIJAR se reemplaza por el panel — ahí el gesto ya terminó
 * (`useHoldGesture` cierra el hold en el lock), así que el swap es seguro.
 *
 * MODO CHARLA (`freeChat`): con el campo vacío, el botón de enviar del campo
 * (que estaría deshabilitado) se convierte en la entrada al modo a manos
 * libres; activo, el composer entero es la barra viva (waveform + estado +
 * mute + salir) y los turnos se cierran solos por VAD.
 */
export function VoiceComposer({
  recorder,
  holdDisabled,
  micHidden,
  textHidden,
  placeholder,
  onSendText,
  labels,
  freeChat,
  freeStatus,
  turnClosedTick,
  leading,
  fieldStart,
  disabled,
}: VoiceComposerProps): ReactElement {
  const dead = disabled === true;
  const cuesOn = turnClosedTick !== undefined && freeChat?.active === true;
  const cues = useListeningCues({
    enabled: cuesOn,
    getEnergy: freeChat?.getEnergy ?? zeroEnergy,
    status: freeStatus ?? 'listening',
    muted: freeChat?.muted === true,
    turnClosedTick: turnClosedTick ?? 0,
  });
  if (cuesOn && labels.heard === undefined) {
    throw new Error('VoiceComposer: turnClosedTick necesita labels.heard');
  }
  const [text, setText] = useState('');

  // Descarte con show (paridad WhatsApp): todo cancel — soltar con "cancelar"
  // armado o el tacho del panel fijado — pasa por acá, que además de cancelar
  // monta el CancelSplash (mic volando al tachito) por ~1.3s. Mientras corre,
  // el composer queda VACÍO; al terminar, el campo + mic reaparecen con su
  // propia entrada (`reborn` → clase vc-reborn, retirada al completarse).
  const [cancelSplash, setCancelSplash] = useState(false);
  const [reborn, setReborn] = useState(false);
  const splashTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (splashTimerRef.current !== null) window.clearTimeout(splashTimerRef.current);
    },
    [],
  );
  const cancelWithSplash = useCallback((): void => {
    recorder.cancel();
    setCancelSplash(true);
    setReborn(false);
    if (splashTimerRef.current !== null) window.clearTimeout(splashTimerRef.current);
    splashTimerRef.current = window.setTimeout(() => {
      setCancelSplash(false);
      setReborn(true);
      splashTimerRef.current = window.setTimeout(() => {
        splashTimerRef.current = null;
        setReborn(false);
      }, 450);
    }, 1300);
  }, [recorder]);
  const gestureRecorder = useMemo<HoldRecorder>(
    () => ({ ...recorder, cancel: cancelWithSplash }),
    [recorder, cancelWithSplash],
  );

  const { cancelArmed, handlers } = useHoldGesture(gestureRecorder, holdDisabled);
  const { recording, locked, paused, interrupted } = recorder;

  // Escudo anti GHOST CLICK del panel fijado: el tap que FIJA termina con un
  // click sintético del browser (post-pointerup, resuelto por coordenadas)
  // que aterriza sobre lo que haya AHORA en ese lugar — y ahí ya se montó el
  // panel. Según dónde tocaste el mic, ese click fantasma caía en enviar
  // (mandaba el turno al toque), pausa o el tacho — "el candado no funciona".
  // El ref marca el montaje del panel EN RENDER (antes de que el click
  // llegue) y el onClickCapture de abajo se traga cualquier click de esa
  // ventana.
  const panelMountAtRef = useRef<number | null>(null);
  if (recording && locked) {
    if (panelMountAtRef.current === null) panelMountAtRef.current = performance.now();
  } else {
    panelMountAtRef.current = null;
  }

  // `micHidden` y `textHidden` juntos no ocurren (micDenied fuerza el canal
  // efectivo a texto en el caller); si llegara a pasar, degradamos a mostrar
  // el campo de texto — un composer sin ninguna entrada sería inusable.
  const hideText = textHidden === true && !micHidden;

  const sendText = (): void => {
    if (dead) return;
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    onSendText(trimmed);
    setText('');
  };

  // ── Modo charla activo: la barra viva reemplaza al composer entero ────────
  if (freeChat?.active === true) {
    const status = freeChat.muted
      ? labels.micMuted
      : cues.heard && labels.heard !== undefined
        ? labels.heard
        : freeStatus === 'speaking'
        ? labels.speaking
        : freeStatus === 'thinking'
          ? labels.thinking
          : labels.listening;
    return (
      <div className="vc">
        <div
          className={
            'vc-freebar' +
            (freeChat.muted ? ' is-muted' : '') +
            (cuesOn ? ' has-cues' : '') +
            (cues.nodding ? ' is-nodding' : '') +
            (cues.heard ? ' is-heard' : '')
          }
        >
          <span className="vc-free-dot" aria-hidden />
          <span className="vc-free-status" role="status">
            {status}
          </span>
          <LiveWaveform getEnergy={freeChat.getEnergy} barCount={18} />
        </div>
        <button
          type="button"
          className={'vc-round vc-free-mute' + (freeChat.muted ? ' is-muted' : '')}
          onClick={freeChat.toggleMute}
          aria-pressed={freeChat.muted}
          aria-label={freeChat.muted ? labels.unmute : labels.mute}
        >
          {freeChat.muted ? (
            <MicOffIcon width={18} height={18} />
          ) : (
            <MicIcon width={18} height={18} />
          )}
        </button>
        <button
          type="button"
          className="vc-round vc-free-exit"
          onClick={freeChat.exit}
          aria-label={labels.exitFreeChat}
        >
          <XIcon width={16} height={16} />
        </button>
      </div>
    );
  }

  // ── Grabación fijada: panel WhatsApp de dos filas ──────────────────────────
  if (recording && locked) {
    return (
      <div className="vc">
        <div
          className="vc-lockpanel"
          onClickCapture={(e) => {
            const at = panelMountAtRef.current;
            if (at !== null && performance.now() - at < GHOST_CLICK_MS) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          {/* Grabando: dot + tiempo + ventana deslizante del historial real.
              Pausado: zoom out — play + take entero con progreso + tiempo. */}
          {paused ? (
            <div className="vc-lockpanel-top">
              {/* Snapshot del historial (el buffer vivo muta por push): en
                  pausa está congelado y el composer casi no re-renderiza.
                  El tiempo de la derecha lo pone TakeOverview (total ↔
                  posición de reproducción). */}
              <TakeOverview
                samples={[...recorder.getTakeSamples()]}
                url={recorder.takeUrl}
                durationSec={recorder.elapsed}
                labels={labels}
              />
            </div>
          ) : (
            <div className="vc-lockpanel-top">
              <span className="ptt-dot" aria-hidden />
              <span className="ptt-time">{fmtTime(recorder.elapsed)}</span>
              <ScrollingWave getSamples={recorder.getTakeSamples} />
            </div>
          )}
          {/* Turno cortado por la conexión: el audio quedó sólo acá. Se dice y
              se ofrece la única salida honesta (descartar y regrabar) — enviar
              y seguir grabando desaparecen porque no llegarían a ningún lado. */}
          {interrupted ? (
            <p className="vc-lockpanel-alert" role="alert">
              <strong>{labels.connectionLost}</strong> {labels.audioNotSent}
            </p>
          ) : null}
          <div className="vc-lockpanel-actions">
            <button
              type="button"
              className="vc-trash"
              onClick={cancelWithSplash}
              aria-label={labels.discard}
            >
              <TrashIcon width={19} height={19} />
            </button>
            {interrupted ? null : (
              <>
                <button
                  type="button"
                  className={'vc-round vc-pause' + (paused ? ' is-paused' : '')}
                  onClick={paused ? recorder.resume : recorder.pause}
                  aria-label={paused ? labels.resume : labels.pause}
                >
                  {paused ? (
                    <MicIcon width={18} height={18} />
                  ) : (
                    <PauseIcon width={18} height={18} />
                  )}
                </button>
                <button
                  type="button"
                  className="vc-mic is-send"
                  onClick={recorder.send}
                  aria-label={labels.send}
                >
                  <ArrowRightIcon width={18} height={18} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const hasText = text.trim().length > 0;

  // La entrada al modo charla vive en el lugar del botón de enviar del campo
  // (que con el campo vacío estaría deshabilitado — espacio muerto).
  const showFreeChatEntry = freeChat !== undefined && !micHidden && !recording && !hasText;

  // Paridad WhatsApp: con texto escrito, el botón GRANDE de la derecha deja de
  // ser el mic y pasa a ser enviar (y vuelve a mic al vaciarse el campo). El
  // envío inline del campo entonces sobra — se muestra sólo cuando NO hay
  // botón grande que pueda tomar ese rol (mic oculto: canal texto / mic
  // denegado), para no quedar sin forma de enviar.
  const sendIsMainButton = hasText && !micHidden && !recording;

  // Show de descarte en curso: el composer entero se corre a un costado — sólo
  // el escenario (mantiene el alto) con el mic cayendo al tachito. El campo y
  // el mic vuelven a montarse al terminar, con la entrada `vc-reborn`.
  const splashing = cancelSplash && !recording;

  return (
    <div className="vc">
      {/* Avisos flotantes sobre la barra (misma pieza, se apagan solos):
          - el mic no entrega sonido (HoldRecorder.noSound) — grabando o al
            soltar un turno mudo. Gana sobre el genérico: dice POR QUÉ.
          - el último envío murió sin transcript (HoldRecorder.sendLost). Sin
            esto el usuario ve su audio "desaparecer" sin explicación y no
            sabe que debe regrabar. */}
      {recorder.noSound ? (
        <p className="vc-send-lost" role="status">
          {labels.noSound}
        </p>
      ) : recorder.sendLost ? (
        <p className="vc-send-lost" role="status">
          {labels.nothingHeard}
        </p>
      ) : null}
      {/* Slot `leading`: sólo en el estado default — grabando/splash la fila
          se reemplaza y el slot se va con el campo (cero cambio de layout). */}
      {leading !== undefined && !recording && !splashing ? (
        <div className="vc-leading">{leading}</div>
      ) : null}
      {splashing ? (
        <div className="vc-splashstage" aria-hidden="true">
          <CancelSplash />
        </div>
      ) : recording ? (
        <div className={'vc-recbar' + (cancelArmed ? ' is-canceling' : '')}>
          <RecIndicator
            elapsed={recorder.elapsed}
            getSamples={recorder.getTakeSamples}
            cancelArmed={cancelArmed}
            labels={labels}
          />
        </div>
      ) : hideText ? null : (
        <div
          className={
            'vc-field' +
            (reborn ? ' vc-reborn' : '') +
            // Sin botón adentro (el envío se fue al botón grande): el pill
            // recupera el padding derecho de texto, si no el cursor queda
            // pegado al borde.
            (sendIsMainButton ? ' vc-field--noend' : '')
          }
        >
          {fieldStart !== undefined ? <span className="vc-fieldstart">{fieldStart}</span> : null}
          <input
            type="text"
            value={text}
            disabled={dead}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') sendText();
            }}
            placeholder={placeholder}
            aria-label={placeholder}
          />
          {showFreeChatEntry ? (
            <button
              type="button"
              className="vc-send vc-send--free-chat"
              onClick={freeChat.enter}
              disabled={holdDisabled || dead}
              aria-label={labels.freeChat}
              title={labels.freeChat}
            >
              <WaveformIcon width={16} height={16} />
            </button>
          ) : sendIsMainButton ? null : (
            <button
              type="button"
              className="vc-send"
              onClick={sendText}
              disabled={dead || !hasText}
              aria-label={labels.send}
            >
              <ArrowRightIcon width={16} height={16} />
            </button>
          )}
        </div>
      )}

      {splashing || micHidden ? null : sendIsMainButton ? (
        /* Con texto en el campo el botón grande es ENVIAR (no hay gesto de
           hold que preservar acá: no se puede tipear grabando). `holdDisabled`
           no lo apaga — el texto viaja aunque el turno de voz esté en vuelo. */
        <button
          type="button"
          className="vc-mic is-send"
          onClick={sendText}
          disabled={dead}
          aria-label={labels.send}
        >
          <ArrowRightIcon width={18} height={18} />
        </button>
      ) : (
        <>
          {recording && !cancelArmed ? (
            <span className="vc-lockhint" aria-hidden>
              <LockIcon width={13} height={13} />
              <span className="vc-lockhint-arrow">↑</span>
            </span>
          ) : null}
          {/* Sólo-PTT: fuera de la grabación el botón se estira a pill de ancho
              completo (mismo elemento — el pointer capture del hold persiste). */}
          <button
            type="button"
            className={
              'vc-mic' +
              (recording ? ' recording' : '') +
              (hideText && !recording ? ' vc-mic--wide' : '') +
              (reborn && !recording ? ' vc-reborn' : '')
            }
            disabled={holdDisabled || dead}
            aria-label={
              recording
                ? cancelArmed
                  ? labels.releaseToCancel
                  : labels.releaseToSend
                : labels.hold
            }
            {...handlers}
          >
            <MicIcon />
            {hideText && !recording ? (
              <span className="vc-mic-label">{labels.hold}</span>
            ) : null}
          </button>
        </>
      )}
    </div>
  );
}
